import { useEffect, useRef, useState } from 'react';
import { Form, ListGroup, Spinner } from 'react-bootstrap';
import { addressAutocomplete, addressPlaceDetails } from '../api';

// A single-field, Google-Maps-backed "searchable dropdown" for a free-text area/locality — the
// lightweight cousin of AddressAutocomplete (which builds a full structured postal address).
// Typing shows live place suggestions (rentalapi's /api/maps proxy, no Google key in the
// browser); picking one stores the formatted description + lat/lng/placeId. Typing without
// picking still works — it just clears the geocoding (same "refine text is fine, only a real
// locality change invalidates the pin" rule AddressAutocomplete follows).
//
// onChange({ area, areaLat, areaLng, areaPlaceId })

function newSessionToken() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  } catch { /* ignore */ }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function LocalityAutocomplete({ value = '', onChange, idPrefix = 'locality', size = 'sm', placeholder = 'Search area / locality…' }) {
  const [query, setQuery] = useState(value || '');
  const [predictions, setPredictions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const sessionToken = useRef(newSessionToken());
  const blurTimer = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);
  useEffect(() => () => clearTimeout(blurTimer.current), []);

  useEffect(() => {
    if (query.trim().length < 3) { setPredictions([]); setOpen(false); return undefined; }
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const list = await addressAutocomplete(query.trim(), sessionToken.current);
        setPredictions(list);
        setOpen(list.length > 0);
      } catch {
        setPredictions([]);
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [query]);

  const pick = async (p) => {
    setOpen(false);
    setLoading(true);
    try {
      const addr = await addressPlaceDetails(p.placeId, sessionToken.current);
      const label = addr?.formatted || [p.mainText, p.secondaryText].filter(Boolean).join(', ');
      setQuery(label);
      onChange({ area: label, areaLat: addr?.lat ?? null, areaLng: addr?.lng ?? null, areaPlaceId: p.placeId || '' });
    } catch {
      setQuery(p.mainText);
      onChange({ area: p.mainText, areaLat: null, areaLng: null, areaPlaceId: '' });
    } finally {
      sessionToken.current = newSessionToken();
      setLoading(false);
    }
  };

  const handleTyping = (e) => {
    const text = e.target.value;
    setQuery(text);
    // Free typing without picking a suggestion doesn't carry coordinates.
    onChange({ area: text, areaLat: null, areaLng: null, areaPlaceId: '' });
  };

  return (
    <div className="position-relative">
      <Form.Control
        id={`${idPrefix}-search`}
        size={size}
        type="search"
        placeholder={placeholder}
        value={query}
        autoComplete="off"
        onChange={handleTyping}
        onFocus={() => predictions.length > 0 && setOpen(true)}
        onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 150); }}
      />
      {loading && (
        <Spinner animation="border" size="sm" className="position-absolute" style={{ right: 10, top: 6 }} role="status" />
      )}
      {open && predictions.length > 0 && (
        <ListGroup className="position-absolute w-100 shadow-sm" style={{ zIndex: 1056, maxHeight: 220, overflowY: 'auto' }}>
          {predictions.map((p) => (
            <ListGroup.Item key={p.placeId} action type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => pick(p)}>
              <div className="small fw-semibold">{p.mainText}</div>
              {p.secondaryText && <div className="small text-muted">{p.secondaryText}</div>}
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}
    </div>
  );
}
